import type {
  LoyaltyRewardDiscountFulfillment,
  LoyaltyRewardFreeProductFulfillment,
} from "@/server/platform/db/schema/crm/loyalty-reward";
import type { CachedLoyaltyRewardRecord } from "./types";

export type LoyaltyRewardCartLine = {
  variantId?: string | null;
  lineSubtotal?: number;
  taxTotal?: number;
};

export type ValidateLoyaltyRewardFromCacheInput = {
  reward: CachedLoyaltyRewardRecord;
  customerId?: string;
  effectiveBalance: number;
  orderGrandTotal?: number;
  cartItems?: LoyaltyRewardCartLine[];
  at?: string;
};

export type ValidateLoyaltyRewardFromCacheResult = {
  valid: boolean;
  error?: string;
  discountAmount?: number;
  pointsCost: number;
  balanceAfter?: number;
};

export type IsAffordableLoyaltyRewardFromCacheInput = {
  reward: CachedLoyaltyRewardRecord;
  effectiveBalance: number;
  at?: string;
};

const SUPPORTED_REWARD_TYPES = new Set([
  "ORDER_DISCOUNT_AMOUNT",
  "ORDER_DISCOUNT_PERCENT",
  "FREE_PRODUCT",
]);

/** ກວດແຕ່ຄະແນນ + active + ຊ່ວງເວລາ — ບໍ່ກວດຕະກຣ້າ (ໃຊ້ຕອນແສດງລາຍການ) */
export function isAffordableLoyaltyRewardFromCache(
  input: IsAffordableLoyaltyRewardFromCacheInput,
): boolean {
  const at = input.at ?? new Date().toISOString();
  const pointsCost = Number(input.reward.pointsCost);

  if (!input.reward.active) return false;
  if (!SUPPORTED_REWARD_TYPES.has(input.reward.rewardType)) return false;
  if (!isInValidityWindow(input.reward, at)) return false;
  if (input.effectiveBalance < pointsCost) return false;

  return true;
}

function isInValidityWindow(
  reward: CachedLoyaltyRewardRecord,
  atIso: string,
): boolean {
  const atMs = new Date(atIso).getTime();
  if (reward.validFrom) {
    const fromMs = new Date(reward.validFrom).getTime();
    if (!Number.isNaN(fromMs) && atMs < fromMs) return false;
  }
  if (reward.validTo) {
    const toMs = new Date(reward.validTo).getTime();
    if (!Number.isNaN(toMs) && atMs > toMs) return false;
  }
  return true;
}

function computeDiscount(
  reward: CachedLoyaltyRewardRecord,
  orderGrandTotal: number,
  cartItems: LoyaltyRewardCartLine[],
): { discount?: number; error?: string } {
  if (
    reward.rewardType === "ORDER_DISCOUNT_AMOUNT" ||
    reward.rewardType === "ORDER_DISCOUNT_PERCENT"
  ) {
    const f = reward.fulfillment as LoyaltyRewardDiscountFulfillment;
    let amount =
      reward.rewardType === "ORDER_DISCOUNT_PERCENT"
        ? (orderGrandTotal * (Number(f.discountValue) || 0)) / 100
        : Number(f.discountValue) || 0;
    amount = Math.min(amount, orderGrandTotal);
    if (f.maxDiscountAmount != null && f.maxDiscountAmount > 0) {
      amount = Math.min(amount, Number(f.maxDiscountAmount));
    }
    return { discount: Math.round(amount * 100) / 100 };
  }

  if (reward.rewardType === "FREE_PRODUCT") {
    const f = reward.fulfillment as LoyaltyRewardFreeProductFulfillment;
    const variantId = f.productVariantId?.trim();
    if (!variantId) {
      return { error: "ການຕັ້ງຄ່າລາງວັນບໍ່ຖືກຕ້ອງ" };
    }
    const match = cartItems.find((i) => i.variantId === variantId);
    if (!match) {
      return { error: "ບໍ່ມີສິນຄ້າລາງວັນໃນຕະກຣ້າ" };
    }
    const lineTotal =
      Math.round(
        (Number(match.lineSubtotal ?? 0) + Number(match.taxTotal ?? 0)) * 100,
      ) / 100;
    if (lineTotal <= 0) {
      return { error: "ບໍ່ສາມາດຄິດສ່ວນຫຼຸດຈາກລາຍການໄດ້" };
    }
    return { discount: lineTotal };
  }

  return { error: "ປະເພດລາງວັນນີ້ຍັງບໍ່ຮອງຮັບ offline" };
}

export function validateLoyaltyRewardFromCache(
  input: ValidateLoyaltyRewardFromCacheInput,
): ValidateLoyaltyRewardFromCacheResult {
  const at = input.at ?? new Date().toISOString();
  const pointsCost = Number(input.reward.pointsCost);

  if (!input.reward.active) {
    return { valid: false, error: "ລາງວັນນີ້ປິດໃຊ້ງານແລ້ວ", pointsCost };
  }
  if (!isInValidityWindow(input.reward, at)) {
    return { valid: false, error: "ລາງວັນຢູ່ນອກຊ່ວງເວລາທີ່ໃຊ້ໄດ້", pointsCost };
  }
  if (input.effectiveBalance < pointsCost) {
    return {
      valid: false,
      error: "ຄະແນນບໍ່ພຽງພໍ (ຈາກ cache)",
      pointsCost,
      balanceAfter: Math.round((input.effectiveBalance - pointsCost) * 100) / 100,
    };
  }

  const orderGrandTotal =
    input.orderGrandTotal ??
    input.cartItems?.reduce(
      (s, i) => s + Number(i.lineSubtotal ?? 0) + Number(i.taxTotal ?? 0),
      0,
    ) ??
    0;

  const discountResult = computeDiscount(
    input.reward,
    orderGrandTotal,
    input.cartItems ?? [],
  );
  if (discountResult.error) {
    return { valid: false, error: discountResult.error, pointsCost };
  }
  if (!discountResult.discount || discountResult.discount <= 0) {
    return { valid: false, error: "ບໍ່ສາມາດຄິດສ່ວນຫຼຸດໄດ້", pointsCost };
  }

  return {
    valid: true,
    discountAmount: discountResult.discount,
    pointsCost,
    balanceAfter: Math.round((input.effectiveBalance - pointsCost) * 100) / 100,
  };
}
